import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Video, FileText, Play, CheckCircle2, X, Lock } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import BadgeCelebration from './BadgeCelebration';

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}
function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(); };
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    });
  }
  return ytApiPromise;
}

let vimeoApiPromise = null;
function loadVimeoApi() {
  if (window.Vimeo) return Promise.resolve();
  if (!vimeoApiPromise) {
    vimeoApiPromise = new Promise((resolve) => {
      const tag = document.createElement('script');
      tag.src = 'https://player.vimeo.com/api/player.js';
      tag.onload = () => resolve();
      document.head.appendChild(tag);
    });
  }
  return vimeoApiPromise;
}

// Tracked video player: accumulates real watched seconds (seeking ahead doesn't count)
// and calls onUnlock once >= 90% of the video has actually been watched.
// Progress is persisted to the backend via heartbeats so it survives reloads
// and is enforced server-side.
const WATCH_THRESHOLD = 0.9;
const HEARTBEAT_INTERVAL_MS = 5000; // flush pending delta every 5s

function VideoWatchPlayer({ item, childId, initialSeconds = 0, initialDuration = 0, onProgress, onUnlock }) {
  const hostRef = useRef(null);
  const watchedRef = useRef(initialSeconds);
  const durationRef = useRef(initialDuration);
  const lastTimeRef = useRef(0);
  const doneRef = useRef(false);
  const pendingDeltaRef = useRef(0);
  const cbRef = useRef({ onProgress, onUnlock });
  cbRef.current = { onProgress, onUnlock };

  const ytId = getYouTubeId(item.url);
  const vimeoId = getVimeoId(item.url);

  const flushHeartbeat = async () => {
    const delta = pendingDeltaRef.current;
    const duration = durationRef.current;
    if (delta <= 0 || !childId) return;
    pendingDeltaRef.current = 0;
    try {
      const { data } = await api.post(`/api/content/${item.id}/watch-heartbeat`, {
        child_id: childId,
        delta_seconds: delta,
        duration: duration || 0,
        current_time: lastTimeRef.current,
      });
      // Trust server-side accumulated value (prevents local-only inflation)
      if (data?.watched_seconds != null) watchedRef.current = data.watched_seconds;
      if (data?.duration) durationRef.current = data.duration;
      const pct = Math.round(data?.percent ?? 0);
      cbRef.current.onProgress?.(pct, watchedRef.current, durationRef.current);
      if (data?.unlocked && !doneRef.current) {
        doneRef.current = true;
        cbRef.current.onUnlock?.();
      }
    } catch {
      // Put the delta back so we try again on next tick
      pendingDeltaRef.current += delta;
    }
  };

  const accumulate = (currentTime, duration) => {
    if (duration) durationRef.current = duration;
    if (!duration || doneRef.current) { lastTimeRef.current = currentTime; return; }
    const delta = currentTime - lastTimeRef.current;
    if (delta > 0 && delta <= 3) {
      pendingDeltaRef.current += delta;
      // Optimistic local update for a snappy progress bar
      const localWatched = watchedRef.current + pendingDeltaRef.current;
      const pct = Math.min(100, Math.round((localWatched / duration) * 100));
      cbRef.current.onProgress?.(pct, localWatched, duration);
    }
    lastTimeRef.current = currentTime;
  };

  const handleEnded = async () => {
    // Flush any pending seconds and request server unlock check.
    await flushHeartbeat();
    // If server-tracked seconds still fall short (e.g. heavy scrubbing) do not unlock.
    if (!doneRef.current
        && durationRef.current > 0
        && watchedRef.current >= durationRef.current * WATCH_THRESHOLD) {
      doneRef.current = true;
      cbRef.current.onProgress?.(100, watchedRef.current, durationRef.current);
      cbRef.current.onUnlock?.();
    }
  };

  useEffect(() => {
    let player = null;
    let interval = null;
    let heartbeatTimer = null;
    let destroyed = false;

    heartbeatTimer = setInterval(flushHeartbeat, HEARTBEAT_INTERVAL_MS);

    if (ytId) {
      loadYouTubeApi().then(() => {
        if (destroyed || !hostRef.current) return;
        player = new window.YT.Player(hostRef.current, {
          videoId: ytId,
          playerVars: { rel: 0 },
          events: {
            onStateChange: (e) => {
              if (e.data === window.YT.PlayerState.ENDED) handleEnded();
            },
          },
        });
        interval = setInterval(() => {
          if (!player || !player.getCurrentTime) return;
          if (player.getPlayerState && player.getPlayerState() === window.YT.PlayerState.PLAYING) {
            accumulate(player.getCurrentTime(), player.getDuration());
          } else {
            lastTimeRef.current = player.getCurrentTime();
          }
        }, 1000);
      });
    } else if (vimeoId) {
      loadVimeoApi().then(() => {
        if (destroyed || !hostRef.current) return;
        player = new window.Vimeo.Player(hostRef.current, { url: item.url, responsive: true });
        player.on('timeupdate', (d) => accumulate(d.seconds, d.duration));
        player.on('ended', handleEnded);
      });
    }

    return () => {
      destroyed = true;
      if (interval) clearInterval(interval);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      // One last flush on unmount so progress isn't lost
      flushHeartbeat();
      if (player && player.destroy) { try { player.destroy(); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, childId]);

  if (ytId || vimeoId) {
    return (
      <div
        ref={hostRef}
        className="w-full h-full rounded-2xl overflow-hidden bg-black [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:rounded-2xl"
        data-testid="content-video-iframe"
      />
    );
  }

  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const videoUrl = item.url?.startsWith('http') ? item.url : `${backend}${item.url}`;
  return (
    <video
      controls
      className="w-full h-full rounded-2xl bg-black"
      data-testid="content-video-player"
      onTimeUpdate={(e) => accumulate(e.target.currentTime, e.target.duration)}
      onEnded={handleEnded}
    >
      <source src={videoUrl} />
    </video>
  );
}

const TYPE_STYLES = {
  video:    { bg: 'bg-[#E0F2FE]', fg: 'text-[#0284C7]', icon: Video,     label: 'Video' },
  pdf:      { bg: 'bg-[#FFEDD5]', fg: 'text-[#FD5E53]', icon: FileText,  label: 'PDF'   },
  activity: { bg: 'bg-[#DCFCE7]', fg: 'text-[#16A34A]', icon: BookOpen,  label: 'Activity' },
};

export default function ContentViewer({ child, isSubscribed = false, onSubscribeClick }) {
  const [content, setContent] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressMap, setProgressMap] = useState({});
  const [celebrateBadges, setCelebrateBadges] = useState([]);
  const watchSecondsRef = useRef({});
  const watchDurationRef = useRef({});
  const unlockedRef = useRef({});
  const [watchPct, setWatchPct] = useState(0);
  const [watchUnlocked, setWatchUnlocked] = useState(false);

  const openItem = async (item) => {
    setSelected(item);
    setWatchPct(0);
    setWatchUnlocked(!!unlockedRef.current[item.id]);
    // Hydrate from server (survives page reload) for videos
    if (item.content_type === 'video' && child?.id) {
      try {
        const { data } = await api.get(`/api/content/${item.id}/watch-session?child_id=${child.id}`);
        watchSecondsRef.current[item.id] = data.watched_seconds || 0;
        watchDurationRef.current[item.id] = data.duration || 0;
        unlockedRef.current[item.id] = !!data.unlocked;
        setWatchPct(Math.round(data.percent || 0));
        setWatchUnlocked(!!data.unlocked);
      } catch { /* non-fatal, start from zero */ }
    }
  };

  const handleWatchProgress = (pct, seconds, duration) => {
    if (!selected) return;
    watchSecondsRef.current[selected.id] = seconds;
    if (duration) watchDurationRef.current[selected.id] = duration;
    setWatchPct(pct);
  };

  const handleWatchUnlock = () => {
    if (!selected) return;
    unlockedRef.current[selected.id] = true;
    setWatchUnlocked(true);
  };

  useEffect(() => {
    if (!child?.grade) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/content?grade=${encodeURIComponent(child.grade)}`);
        setContent(data);
      } catch { toast.error('Failed to load lessons'); }
      finally { setLoading(false); }
    })();
  }, [child?.grade]);

  const markComplete = async (item) => {
    try {
      const { data } = await api.post('/api/progress', {
        child_id: child.id, content_id: item.id,
        completed: true, progress_percentage: 100,
      });
      setProgressMap((p) => ({ ...p, [item.id]: true }));
      toast.success('Marked as completed! +5 points');
      if (data?.new_badges?.length > 0) {
        setTimeout(() => setCelebrateBadges(data.new_badges), 500);
      }
    } catch { toast.error('Failed to mark complete'); }
  };

  const renderPlayer = (item) => {
    if (item.content_type === 'video') {
      return (
        <VideoWatchPlayer
          item={item}
          childId={child?.id}
          initialSeconds={watchSecondsRef.current[item.id] || 0}
          initialDuration={watchDurationRef.current[item.id] || 0}
          onProgress={handleWatchProgress}
          onUnlock={handleWatchUnlock}
        />
      );
    }
    if (item.content_type === 'pdf') {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const pdfUrl = item.url?.startsWith('http') ? item.url : `${backend}${item.url}`;
      return (
        <div className="relative w-full h-full">
          <iframe
            src={`${pdfUrl}#view=FitH&toolbar=1`}
            title={item.title}
            className="w-full h-full rounded-2xl bg-white"
            data-testid="content-pdf-viewer"
          />
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 bg-[#FD5E53] hover:bg-[#E84E44] text-white px-4 py-2 rounded-full font-fredoka font-medium text-sm shadow-lg"
          >
            Open in new tab ↗
          </a>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-full bg-slate-50 rounded-2xl">
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[#FD5E53] font-fredoka font-medium underline">
          Open activity
        </a>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-[#38BDF8] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="content-viewer">
      <div className="mb-6">
        <h2 className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
          Lessons for {child.name}
        </h2>
        <p className="font-nunito text-slate-500">
          Grade {child.grade} · {content.length} lesson{content.length !== 1 ? 's' : ''} available
        </p>
      </div>

      {!isSubscribed && content.length > 0 && (
        <div
          className="mb-5 rounded-3xl border border-[#FDE68A] bg-gradient-to-r from-[#FEF9C3] to-[#FED7AA] p-5 flex flex-wrap items-center gap-4 justify-between"
          data-testid="content-locked-banner"
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-2xl bg-white/70 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-[#D97706]" />
            </div>
            <div className="min-w-0">
              <p className="font-fredoka text-slate-800 text-base font-semibold leading-snug">
                Subscribe to unlock lessons
              </p>
              <p className="font-nunito text-sm text-slate-600">
                You can see all {content.length} lesson{content.length !== 1 ? 's' : ''} — subscribe to start learning.
              </p>
            </div>
          </div>
          {onSubscribeClick && (
            <button
              onClick={onSubscribeClick}
              className="bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-sm px-5 py-2.5 rounded-full shadow-sm transition-all hover:-translate-y-0.5"
              data-testid="content-unlock-subscribe-btn"
            >
              Subscribe now
            </button>
          )}
        </div>
      )}

      {content.length === 0 ? (
        <div className="bg-white rounded-[2rem] p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50">
          <BookOpen className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="font-nunito text-slate-500">No lessons available for this grade yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {content.map((item, i) => {
            const style = TYPE_STYLES[item.content_type] || TYPE_STYLES.activity;
            const Icon = style.icon;
            const completed = progressMap[item.id];
            return (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={isSubscribed ? { y: -4 } : {}}
                onClick={() => {
                  if (!isSubscribed) {
                    toast.info('Subscribe to unlock lessons');
                    onSubscribeClick?.();
                    return;
                  }
                  openItem(item);
                }}
                className={`text-left bg-white rounded-[1.75rem] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50 transition-all ${
                  isSubscribed ? 'hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]' : 'opacity-80 cursor-not-allowed'
                }`}
                data-testid={`content-card-${item.id}`}
              >
                <div className={`h-28 rounded-2xl ${style.bg} flex items-center justify-center relative overflow-hidden mb-4`}>
                  <Icon className={`w-12 h-12 ${style.fg}`} strokeWidth={1.5} />
                  {!isSubscribed && (
                    <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] flex items-center justify-center">
                      <div className="bg-white/95 rounded-full p-2 shadow">
                        <Lock className="w-5 h-5 text-slate-700" />
                      </div>
                    </div>
                  )}
                  {completed && (
                    <span className="absolute top-2 right-2 bg-white rounded-full px-2 py-0.5 text-[10px] font-fredoka font-medium text-[#16A34A] flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Done
                    </span>
                  )}
                  <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-wider font-nunito font-bold text-slate-700/80">
                    {style.label}
                  </span>
                </div>
                <h3 className="font-fredoka text-base font-medium text-slate-800 mb-1 line-clamp-1">{item.title}</h3>
                {item.description && (
                  <p className="font-nunito text-xs text-slate-500 line-clamp-2 mb-3">{item.description}</p>
                )}
                <div className={`inline-flex items-center gap-1.5 text-xs font-fredoka font-medium ${isSubscribed ? 'text-[#FD5E53]' : 'text-slate-400'}`}>
                  {isSubscribed ? (<><Play className="w-3.5 h-3.5" /> Open lesson</>) : (<><Lock className="w-3.5 h-3.5" /> Locked</>)}
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="content-modal"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-[2rem] w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div className="min-w-0">
                <h3 className="font-fredoka text-lg font-semibold text-slate-800 truncate">{selected.title}</h3>
                {selected.description && <p className="font-nunito text-sm text-slate-500 truncate">{selected.description}</p>}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0"
                data-testid="content-close-btn"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="flex-1 p-4 bg-slate-900 min-h-[60vh]">
              {renderPlayer(selected)}
            </div>
            <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-4 flex-wrap">
              {selected.content_type === 'video' && !progressMap[selected.id] && (
                <div className="flex-1 min-w-[200px]" data-testid="video-watch-progress">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${watchUnlocked ? 'bg-[#16A34A]' : 'bg-[#38BDF8]'}`}
                      style={{ width: `${watchUnlocked ? 100 : watchPct}%` }}
                    />
                  </div>
                  <p className="font-nunito text-xs text-slate-500 mt-1.5" data-testid="video-watch-text">
                    {watchUnlocked
                      ? 'Full video watched! Tap "Mark as Complete" to earn your points.'
                      : `Watched ${watchPct}% — finish the video to unlock your points`}
                  </p>
                </div>
              )}
              <button
                onClick={() => markComplete(selected)}
                disabled={progressMap[selected.id] || (selected.content_type === 'video' && !watchUnlocked)}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white px-5 py-2.5 rounded-full font-fredoka font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="mark-complete-btn"
              >
                {selected.content_type === 'video' && !watchUnlocked && !progressMap[selected.id] && (
                  <Lock className="w-4 h-4" />
                )}
                {progressMap[selected.id]
                  ? 'Completed ✓'
                  : selected.content_type === 'video' && !watchUnlocked
                    ? 'Watch video to unlock'
                    : 'Mark as Complete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {celebrateBadges.length > 0 && (
        <BadgeCelebration badges={celebrateBadges} onClose={() => setCelebrateBadges([])} />
      )}
    </div>
  );
}
